import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Carte des lieux de travail (lot D de Yros/Kavero#369).
 *
 * Elle ne stocke rien : elle lit `lieux` et rend des coordonnées au parent,
 * qui reste seul propriétaire de l'état du formulaire. Le contrôle de zone
 * continue de comparer des coordonnées, hors ligne — la carte n'est qu'une
 * aide à la saisie.
 *
 * **Aucun fond de carte n'est chargé par défaut.** Le CI Cypress n'a aucun
 * réseau et toute ressource externe y fait échouer les tests
 * (`.aidlc/contexte-projet.md`). Le fond ne s'ajoute donc que si
 * `REACT_APP_CARTE_TUILES` est renseigné — et le choix du fournisseur, comme
 * le coût de sa clé, est la décision PO que le ticket réserve. Sans fond, la
 * carte reste utilisable : positions relatives, cercles de zone, clic pour
 * placer.
 */

// Vue par défaut quand aucun lieu n'est configuré : le monde. Un centre par
// défaut sur une ville donnerait à croire que le point est déjà quelque part,
// alors que rien n'est saisi — même piège que des champs préremplis.
const VUE_MONDE = { centre: [0, 0], zoom: 2 };
// Zoom d'arrivée sur un lieu unique : assez serré pour qu'un rayon de
// quelques centaines de mètres soit lisible.
const ZOOM_LIEU_UNIQUE = 15;

const TUILES = process.env.REACT_APP_CARTE_TUILES;
const TUILES_ATTRIBUTION = process.env.REACT_APP_CARTE_ATTRIBUTION || '';

// Même règle que le formulaire : une chaîne vide n'est pas un zéro légitime.
const enNombre = (valeur) => (String(valeur).trim() === '' ? NaN : Number(valeur));

// Un lieu n'est plaçable que si ses deux coordonnées sont des nombres finis
// dans leurs bornes. Le rayon peut manquer — on dessine alors le point sans
// cercle, ce qui est précisément ce qu'il faut voir sur un lieu non configuré.
const positionsDe = (lieux) => lieux.map(positionDe).filter(Boolean);

const positionDe = (lieu) => {
  const latitude = enNombre(lieu.latitude);
  const longitude = enNombre(lieu.longitude);
  if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) return null;
  if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) return null;
  return [latitude, longitude];
};

function CarteLieux({ lieux, cleActive, onPosition, recentrage }) {
  const { t } = useTranslation();
  const conteneur = useRef(null);
  const carte = useRef(null);
  const calques = useRef([]);
  // Infobulles indexées par clé de lieu : mises à jour en place quand un nom
  // change, sans reconstruire un seul calque.
  const infobulles = useRef({});
  const cadree = useRef(false);
  const dernierRecentrage = useRef(0);
  // Le gestionnaire de clic est posé une seule fois, mais il doit voir la
  // valeur COURANTE de `cleActive` : capturée dans la fermeture au montage,
  // elle resterait figée sur le premier lieu sélectionné.
  const contexte = useRef({ cleActive, onPosition });
  contexte.current = { cleActive, onPosition };

  useEffect(() => {
    if (carte.current || !conteneur.current) return undefined;
    const instance = L.map(conteneur.current).setView(VUE_MONDE.centre, VUE_MONDE.zoom);
    if (TUILES) {
      L.tileLayer(TUILES, { attribution: TUILES_ATTRIBUTION }).addTo(instance);
    }
    // Échelle métrique, jamais impériale : tout ce que cet écran manipule est
    // en mètres — le rayon saisi, celui que le contrôle de zone compare. Une
    // échelle donne l'ordre de grandeur d'un coup d'œil, ce qu'un cercle seul
    // ne dit pas.
    L.control.scale({ imperial: false, metric: true }).addTo(instance);

    // Leaflet calcule ses tuiles depuis la taille du conteneur au montage. Le
    // portail a une barre latérale repliable : sans cette resynchronisation,
    // replier la barre élargit le conteneur et laisse des bandes grises.
    let observateur;
    if (typeof ResizeObserver !== 'undefined') {
      observateur = new ResizeObserver(() => instance.invalidateSize());
      observateur.observe(conteneur.current);
    }
    instance.on('click', (evenement) => {
      const { cleActive: cible, onPosition: rappel } = contexte.current;
      if (!cible || !rappel) return;
      rappel(cible, {
        latitude: evenement.latlng.lat,
        longitude: evenement.latlng.lng,
      });
    });
    carte.current = instance;
    return () => {
      if (observateur) observateur.disconnect();
      instance.remove();
      carte.current = null;
    };
  }, []);

  // Retour à la vue d'ensemble. Le cadrage automatique ne joue qu'une fois ;
  // après avoir zoomé sur un site, rien ne ramenait la RH à ses autres lieux.
  const recadrerTout = useCallback(() => {
    const instance = carte.current;
    const positions = positionsDe(lieux);
    if (!instance || !positions.length) return;
    if (positions.length === 1) {
      instance.setView(positions[0], ZOOM_LIEU_UNIQUE);
    } else {
      instance.fitBounds(positions, { padding: [30, 30] });
    }
  }, [lieux]);

  // Ce que la carte DESSINE : positions, rayons, lieu actif. Le nom n'en fait
  // pas partie — il ne nourrit qu'une infobulle, mise à jour séparément.
  //
  // Sans cette signature, l'effet dépendait de `lieux`, dont l'identité change
  // à chaque frappe : taper un nom reconstruisait tous les calques, caractère
  // par caractère, sous le champ en cours de saisie.
  const dessin = useMemo(
    () => JSON.stringify(
      lieux.map((lieu) => [lieu.cle, lieu.latitude, lieu.longitude, lieu.rayon_m])
    ),
    [lieux]
  );

  useEffect(() => {
    const instance = carte.current;
    if (!instance) return;

    calques.current.forEach((calque) => instance.removeLayer(calque));
    calques.current = [];
    infobulles.current = {};

    const positions = [];
    lieux.forEach((lieu) => {
      const position = positionDe(lieu);
      if (!position) return;
      positions.push(position);
      const actif = lieu.cle === cleActive;

      // `circleMarker` et non `marker` : le marqueur par défaut de Leaflet
      // charge ses icônes en PNG, donc des requêtes — inacceptable sous une
      // CI sans réseau, et cassé par le bundler faute de chemin résolu.
      const marqueur = L.circleMarker(position, {
        radius: 6,
        weight: actif ? 3 : 2,
        color: actif ? '#b45309' : '#0f766e',
        fillOpacity: 1,
      }).bindTooltip(lieu.nom || t('carteLieux.lieuSansNom')).addTo(instance);
      infobulles.current[lieu.cle] = marqueur;
      calques.current.push(marqueur);

      const rayon = enNombre(lieu.rayon_m);
      if (Number.isFinite(rayon) && rayon > 0) {
        // `L.circle` prend un rayon en MÈTRES et le projette selon la
        // latitude. Un cercle en pixels mentirait dès qu'on s'éloigne de
        // l'équateur — et c'est exactement la valeur que le contrôle de zone
        // compare.
        calques.current.push(
          L.circle(position, {
            radius: rayon,
            weight: 1,
            color: actif ? '#b45309' : '#0f766e',
            fillOpacity: 0.12,
          }).addTo(instance)
        );
      }
    });

    // Cadrage une seule fois : recadrer à chaque frappe ferait sauter la vue
    // pendant que la RH saisit ses décimales.
    if (!cadree.current && positions.length) {
      cadree.current = true;
      if (positions.length === 1) {
        instance.setView(positions[0], ZOOM_LIEU_UNIQUE);
      } else {
        instance.fitBounds(positions, { padding: [30, 30] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dessin, cleActive, t]);

  // Les noms seuls : l'infobulle se réécrit en place. C'est ce qui permet de
  // renommer un lieu sans que la carte se reconstruise sous le champ.
  useEffect(() => {
    lieux.forEach((lieu) => {
      const marqueur = infobulles.current[lieu.cle];
      if (marqueur && marqueur.setTooltipContent) {
        marqueur.setTooltipContent(lieu.nom || t('carteLieux.lieuSansNom'));
      }
    });
  }, [lieux, t]);

  // Recadrage explicite, demandé par le parent après un relevé GPS. Le
  // cadrage automatique, lui, ne joue qu'une fois : recadrer à chaque rendu
  // ferait sauter la vue pendant la saisie. Mais un relevé n'est pas une
  // frappe — c'est une demande, et la carte doit y aller.
  useEffect(() => {
    const instance = carte.current;
    if (!instance || !recentrage || recentrage.jeton === dernierRecentrage.current) return;
    const lieu = lieux.find((element) => element.cle === recentrage.cle);
    const position = lieu && positionDe(lieu);
    if (!position) return;
    dernierRecentrage.current = recentrage.jeton;
    instance.setView(position, ZOOM_LIEU_UNIQUE);
  }, [recentrage, lieux]);

  const cadragePossible = positionsDe(lieux).length > 0;

  return (
    <section className="space-y-3 border-t border-slate-100 pt-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">{t('carteLieux.titre')}</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {cleActive ? t('carteLieux.cliquezPourPlacer') : t('carteLieux.choisissezUnLieu')}
          </p>
        </div>
        {cadragePossible && (
          <button
            type="button"
            data-testid="carte-lieux-recadrer"
            onClick={recadrerTout}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            {t('carteLieux.voirTousLesLieux')}
          </button>
        )}
      </div>

      {/* `relative z-0` : Leaflet empile ses panneaux entre 400 et 800. Un
          contexte d'empilement propre garantit qu'ils ne passeront jamais
          au-dessus de la barre latérale ni d'une fenêtre modale. */}
      <div
        data-testid="carte-lieux"
        ref={conteneur}
        className="relative z-0 h-[26rem] sm:h-[32rem] lg:h-[38rem] w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm"
      />

      {!TUILES && (
        <p data-testid="carte-lieux-sans-fond" className="text-xs text-slate-500">
          {t('carteLieux.sansFond')}
        </p>
      )}
    </section>
  );
}

export default CarteLieux;
